import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { translateAuthError } from '../lib/authErrors';
import './Login.css';
import './ResetPassword.css';

const MIN_PASSWORD_LENGTH = 8;

// Supabase renvoie les erreurs du lien de récupération (lien expiré, déjà
// utilisé…) dans le hash ou la query string de l'URL de redirection.
function readUrlError() {
  const params = new URLSearchParams(
    window.location.hash.replace(/^#/, '') || window.location.search
  );
  const code = params.get('error_code') || params.get('error');
  if (!code) return null;
  if (code === 'otp_expired') {
    return 'Ce lien de réinitialisation est invalide ou a expiré. Demandez-en un nouveau.';
  }
  return params.get('error_description') || 'Lien de réinitialisation invalide.';
}

function PasswordInput({ id, label, value, onChange, visible, onToggle, autoComplete }) {
  return (
    <div className="Field">
      <label className="Field-label" htmlFor={id}>{label}</label>
      <div className="Field-passwordWrapper">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          className="Field-input"
          placeholder="••••••••"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          className="Field-toggle"
          onClick={onToggle}
          aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
        >
          {visible ? (
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a20.3 20.3 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a20.3 20.3 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

function ResetPassword() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [urlError] = useState(readUrlError);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [done, setDone] = useState(false);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && password !== confirm;
  const isValid = password.length >= MIN_PASSWORD_LENGTH && password === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || loading) return;

    setErrorMsg(null);
    setLoading(true);
    try {
      // La session de récupération a été ouverte par supabase-js à partir du
      // lien reçu par email (detectSessionInUrl) : updateUser s'applique donc
      // au compte concerné.
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setErrorMsg(translateAuthError(error.code, error.message));
      } else {
        setDone(true);
      }
    } catch {
      setErrorMsg('Erreur réseau. Vérifiez votre connexion.');
    } finally {
      setLoading(false);
    }
  };

  let content;
  if (done) {
    content = (
      <>
        <p className="Reset-success">Votre mot de passe a bien été modifié.</p>
        <button type="button" className="App-submit" onClick={() => navigate('/', { replace: true })}>
          Accéder au portail
        </button>
      </>
    );
  } else if (urlError || !session) {
    content = (
      <>
        <p className="Login-error Reset-block">
          {urlError || 'Ce lien de réinitialisation est invalide ou a expiré. Demandez-en un nouveau.'}
        </p>
        <Link to="/login" className="Reset-link">Retour à la connexion</Link>
      </>
    );
  } else {
    content = (
      <form onSubmit={handleSubmit}>
        {session.user?.email && (
          <p className="Reset-account">Compte : <strong>{session.user.email}</strong></p>
        )}

        <PasswordInput
          id="new-password"
          label="Nouveau mot de passe"
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          visible={showPassword}
          onToggle={() => setShowPassword((v) => !v)}
        />
        <p className={`Reset-hint${tooShort ? ' Reset-hint--error' : ''}`}>
          {MIN_PASSWORD_LENGTH} caractères minimum.
        </p>

        <PasswordInput
          id="confirm-password"
          label="Confirmer le mot de passe"
          autoComplete="new-password"
          value={confirm}
          onChange={setConfirm}
          visible={showPassword}
          onToggle={() => setShowPassword((v) => !v)}
        />
        {mismatch && <p className="Reset-hint Reset-hint--error">Les mots de passe ne correspondent pas.</p>}

        {errorMsg && <p className="Login-error">{errorMsg}</p>}

        <button type="submit" className="App-submit" disabled={!isValid || loading}>
          {loading ? 'Enregistrement…' : 'Modifier le mot de passe'}
        </button>
      </form>
    );
  }

  return (
    <div className="App">
      <div className="App-card">
        <h1 className="App-title">
          My<span>Travo</span>
        </h1>
        <p className="App-subtitle">Réinitialisation du mot de passe</p>
        {content}
      </div>
    </div>
  );
}

export default ResetPassword;
